import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { adminPartnerCommunicationsAPI } from '../services/adminApi';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import { getLocalStorageItem } from '../utils/safeStorage';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #043663;
  }
`;

const ExportControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ExportButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.$primary ? '#059669' : '#043663'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.$primary ? '#047857' : '#032a4a'};
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e5e7eb;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 700;
    color: ${props => props.$color || '#043663'};
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }
  
  .stat-percentage {
    font-size: 0.75rem;
    color: #059669;
    margin-top: 0.25rem;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #1f2937;
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TeamDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ConsentBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$consented ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.$consented ? '#065f46' : '#991b1b'};
`;

const DivisionBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #dbeafe;
  color: #043663;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #d52b1e;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  background: ${props => props.$active ? '#043663' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#043663' : '#f3f4f6'};
    border-color: #043663;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const AdminPartnerCommunicationsSection = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [consentFilter, setConsentFilter] = useState('all');
  const [teamsPerPage, setTeamsPerPage] = useState(20);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch partner communications data
  const { data: communicationsData, isLoading, error } = useQuery(
    ['partnerCommunications', currentPage, teamsPerPage, consentFilter],
    () => adminPartnerCommunicationsAPI.getAll({ 
      page: currentPage, 
      limit: teamsPerPage, 
      consent: consentFilter 
    }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        console.log('🔍 Partner Communications data received:', data);
      },
      onError: (error) => {
        console.error('❌ Partner Communications data error:', error);
      }
    }
  );

  // Reset to page 1 when filter or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [consentFilter, teamsPerPage]);

  const handleExport = async (exportType = 'consented') => {
    try {
      setIsExporting(true);
      
      // Get the admin token from localStorage (same as other admin API calls)
      const token = getLocalStorageItem('adminToken');
      if (!token) {
        throw new Error('No admin authentication token found. Please log in again.');
      }
      
      // Make the API call and get the CSV content directly
      const response = await fetch(`/api/admin/partner-communications/export?consent=${exportType === 'all' ? 'all' : 'consented'}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv,application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Export response error:', response.status, errorText);
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the CSV content as text
      const csvContent = await response.text();
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `partner-communications-${exportType}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ CSV Export successful');
    } catch (error) {
      console.error('❌ Export failed:', error);
      alert(`Export failed: ${error.message}. Please try again.`);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>Loading partner communications data...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>Error loading data: {error.message}</ErrorContainer>
      </Container>
    );
  }

  const { teams, pagination, statistics } = communicationsData?.data || {};

  return (
    <Container>
      <Header>
        <Title>Partner Communications Consent</Title>
        <Controls>
          <FilterSelect 
            value={consentFilter} 
            onChange={(e) => setConsentFilter(e.target.value)}
          >
            <option value="all">All Teams</option>
            <option value="consented">Consented Only</option>
            <option value="not-consented">Not Consented</option>
          </FilterSelect>
          <FilterSelect 
            value={teamsPerPage} 
            onChange={(e) => {
              setTeamsPerPage(parseInt(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </FilterSelect>
          <ExportControls>
            <ExportButton 
              $primary
              onClick={() => handleExport('consented')}
              disabled={isExporting}
            >
              📊 {isExporting ? 'Exporting...' : 'Export Consented Teams (CSV)'}
            </ExportButton>
            <ExportButton 
              onClick={() => handleExport('all')}
              disabled={isExporting}
            >
              📋 {isExporting ? 'Exporting...' : 'Export All Teams (CSV)'}
            </ExportButton>
          </ExportControls>
        </Controls>
      </Header>

      {statistics && (
        <StatsGrid>
          <StatCard>
            <div className="stat-number">{statistics.totalTeams}</div>
            <div className="stat-label">Total Teams</div>
          </StatCard>
          <StatCard $color="#059669">
            <div className="stat-number">{statistics.consentedTeams}</div>
            <div className="stat-label">Consented Teams</div>
            <div className="stat-percentage">
              {statistics.consentRate}% consent rate
            </div>
          </StatCard>
          <StatCard $color="#d52b1e">
            <div className="stat-number">{statistics.notConsentedTeams}</div>
            <div className="stat-label">Not Consented</div>
          </StatCard>
        </StatsGrid>
      )}

      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>Team Name</TableHeaderCell>
              <TableHeaderCell>Team Email</TableHeaderCell>
              <TableHeaderCell>Canton</TableHeaderCell>
              <TableHeaderCell>Division</TableHeaderCell>
              <TableHeaderCell>Consent Status</TableHeaderCell>
              <TableHeaderCell>Members</TableHeaderCell>
              <TableHeaderCell>Joined</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {teams?.length > 0 ? (
              teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell>
                    <TeamName>{team.team_name}</TeamName>
                    <TeamDetails>ID: {team.id.substring(0, 8)}...</TeamDetails>
                  </TableCell>
                  <TableCell>{team.email}</TableCell>
                  <TableCell>{team.canton}</TableCell>
                  <TableCell>
                    {team.division_name ? (
                      <DivisionBadge>{team.division_name}</DivisionBadge>
                    ) : (
                      <span style={{ color: '#d52b1e', fontStyle: 'italic' }}>Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ConsentBadge $consented={team.partner_communications}>
                      {team.partner_communications ? '✓ Consented' : '✗ Not Consented'}
                    </ConsentBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '0.75rem' }}>
                      {team.member_details ? (
                        team.member_details.split(';').map((member, index) => (
                          <div key={index} style={{ marginBottom: '0.25rem' }}>
                            {member.trim()}
                          </div>
                        ))
                      ) : (
                        'No members'
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatDateDDMMYYYY(team.created_at)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  No teams found. Try adjusting your filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {pagination && pagination.pages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {((currentPage - 1) * teamsPerPage) + 1} to {Math.min(currentPage * teamsPerPage, pagination.total)} of {pagination.total} teams
          </PaginationInfo>
          <PaginationControls>
            <PageButton 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </PageButton>
            <PageNumbers>
              {[...Array(pagination.pages)].map((_, idx) => {
                const pageNum = idx + 1;
                // Show first page, last page, current page, and pages around current
                const showPage = 
                  pageNum === 1 || 
                  pageNum === pagination.pages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                
                const showEllipsis = 
                  (pageNum === currentPage - 2 && currentPage > 3) ||
                  (pageNum === currentPage + 2 && currentPage < pagination.pages - 2);
                
                if (showEllipsis) {
                  return <span key={pageNum} style={{ padding: '0.5rem' }}>...</span>;
                }
                
                if (!showPage) return null;
                
                return (
                  <PageButton
                    key={pageNum}
                    $active={currentPage === pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PageButton>
                );
              })}
            </PageNumbers>
            <PageButton 
              onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
              disabled={currentPage === pagination.pages}
            >
              Next →
            </PageButton>
          </PaginationControls>
        </PaginationContainer>
      )}
    </Container>
  );
};

export default AdminPartnerCommunicationsSection;
